import { useEffect, useState } from 'react'

/** UX standard §5: live search waits ~250ms of quiet before acting. */
export const DEFAULT_DEBOUNCE_MS = 250

/**
 * Returns `value` once it has stayed unchanged for `delayMs` milliseconds.
 *
 * The first render returns `value` as-is; every later change restarts the wait,
 * so a burst of keystrokes yields one settled value. Unmounting cancels the
 * pending update.
 *
 * @param value - The fast-changing value (typically an input's text).
 * @param delayMs - Quiet period before the new value is returned. Default 250.
 * @returns The last value that survived a full quiet period.
 *
 * @example
 * ```tsx
 * const term = useDebounce(query.trim())
 * useEffect(() => { search(term) }, [term])
 * ```
 */
export function useDebounce<T>(value: T, delayMs: number = DEFAULT_DEBOUNCE_MS): T {
	const [debounced, setDebounced] = useState(value)

	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), delayMs)
		return () => clearTimeout(timer)
	}, [value, delayMs])

	return debounced
}
